/**
 * Add data-adobe-eventname="cta|interaction|download" to all focusable/clickable elements
 * cta = yellow cta "button"s
 * download = pdf download links
 * interaction = everything else
 *
 * data-adobe-eventcategory="application|follow-through|anchor|contact|product-recommendation"
 *
 * We avoid detecting/scraping with js so CSS/styling/JS changes, for example changing css
 *  class doesn't break tracking, we use data attributes instead
 *
 * https://confluence-general.sebank.se/display/SKITF/OWe+-+Eurocard+XX#OWeEurocardXX-Tracking
 */

const DEBUG = false;

type Domain = "saseurobonusmastercard.se" | "saseurobonusmastercard.no" | "saseurobonusmastercard.dk";
type PageName = Domain | `${Domain}|${string}` | `${Domain}|${string}|${string}`;

type ClickableElement = HTMLAnchorElement | HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLDetailsElement;
type FormElement = HTMLButtonElement | HTMLInputElement | HTMLSelectElement;

type ConfigKey = "trackAccordions" | "trackCta" | "trackInteraction" | "trackDownload";

type AdobePayload = {
  eventName: string;
  eventCategory: string;
  eventAction: string;
  eventLabel: string;
  blockName: string;
  blockId: string;
};

const ASSUME_INTERACTION_IF_NOTHING_SPECIFIED = true;

declare global {
  interface Window {
    // Config from Epi:
    TrackAccordions: boolean;
    TrackCta: boolean;
    TrackInteraction: boolean;
    TrackDownload: boolean;

    // Adobe
    dataLayer: {
      brand: "sas";
      country: "SE" | "NO" | "DK";
      environment: "test" | "dev" | "prod" | "Prod";
      language: "sv" | "no" | "da";
      pageId: `${number}`;
      pageName: PageName;
      pagetype: string;
      scroll: {
        interval: number;
      };
      siteSection1: string;
      siteSection2: string;
      siteSection3: string;
      siteSection4: string;
      siteSection5: string;
      siteSection6: string;
      siteSection7: string;
      url: `https://${Domain}/`;
      website: Domain;
    };
    _satellite: {
      track: (event: string, data: AdobePayload) => void;
    };
  }
}

const isLocalHostStyleguide = window.location.host.includes("localhost:");

if (isLocalHostStyleguide) {
  window.TrackInteraction = true;
  window.TrackCta = true;
  window.TrackAccordions = true;
}

// --- Config from Epi ---
const config = {
  global: {
    trackAccordions: window.TrackAccordions ?? false,
    trackCta: window.TrackCta ?? false,
    trackInteraction: window.TrackInteraction ?? false,
    trackDownload: window.TrackDownload ?? false,
  },
  airplus: {
    trackAccordions: window.TrackAccordions ?? true,
    trackCta: window.TrackCta ?? true,
    trackInteraction: window.TrackInteraction ?? true,
    trackDownload: window.TrackDownload ?? true,
  },
  eurocard: {
    trackAccordions: window.TrackAccordions ?? true,
    trackCta: window.TrackCta ?? true,
    trackInteraction: window.TrackInteraction ?? true,
    trackDownload: window.TrackDownload ?? true,
  },
  spendwise: {
    trackAccordions: window.TrackAccordions ?? true,
    trackCta: window.TrackCta ?? true,
    trackInteraction: window.TrackInteraction ?? true,
    trackDownload: window.TrackDownload ?? true,
  },
};

function getConfig(key: ConfigKey) {
  let brand = "global";
  try {
    if (window.dataLayer && typeof window.dataLayer.brand === "string") {
      brand = window.dataLayer.brand.toLowerCase();
    }
  } catch {
    brand = "global";
  }

  if (config[brand as "global"]?.[key] === undefined) {
    return config.global[key];
  }

  return config[brand as "spendwise"][key];
}

const trackEvent = (base: string, payload: AdobePayload) => {
  try {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`window._satellite.track("${base}", ${JSON.stringify(payload, undefined, 2)})`);
    }
    window._satellite.track(base, payload);
  } catch {
    // ignore
  }
};

const clickableElsString = `
    a[href],
    area[href],
    input:not([disabled]):not([type="hidden"]),
    select:not([disabled]),
    textarea:not([disabled]),
    button:not([disabled]),
    [role="link"][tabindex]:not([tabindex='-1']),
    [role="button"][tabindex]:not([tabindex='-1']),
    [role="input"][tabindex]:not([tabindex='-1']),
    [role="select"][tabindex]:not([tabindex='-1']),
    [contentEditable=true]:not([tabindex='-1']),
    summary
  `.trim();

const ignoredElementCSSQuery = `
    .epi-quickNavigator,
    .epi-editMode,
    .epi-overlay,
    [data-ignore-analytics="true"],
    .external-content-output
  `;

const ignoredRuntimeCSSQuery = `
    [aria-disabled="true"],
    [disabled],
    [data-analytics-ignore],
    details[open] summary
  `;

const formInputElements = new Set(["input", "button", "select", "textarea"]);

function getFileExtension(url?: string) {
  if (typeof url !== "string") {
    return "";
  }
  const parts = url.split(".");
  return parts.length ? parts.pop()!.toLowerCase() : "";
}

function getEventNameFromTarget(target: ClickableElement, useFallback: boolean) {
  const fileExtensions = ["pdf"];
  let eventName = "";

  if (target?.dataset?.adobeEventname) {
    eventName = target.dataset.adobeEventname;
  }

  if (target.getAttribute("href") !== null && fileExtensions.includes(getFileExtension(target.getAttribute("href")!))) {
    eventName = "download";
  }

  if (!eventName && useFallback === true) {
    const type = target.getAttribute("type")?.toLowerCase();
    if (target.classList?.contains("button") || type === "submit") {
      eventName = "cta";
    } else {
      eventName = "interaction";
    }
  }

  return eventName;
}

function getEventCategory(target: ClickableElement) {
  if (target?.dataset?.adobeEventcategory) {
    return target.dataset.adobeEventcategory;
  }
  if (typeof target.getAttribute("href") === "string" && target.getAttribute("href")!.includes("#")) {
    if (target.getAttribute("href")!.startsWith("#") || target.getAttribute("href")!.includes(window.location.pathname)) {
      return "anchor";
    }
  }
  return "";
}

function getFormTextLabel(origTarget: FormElement): string {
  let result = "";
  if (formInputElements.has(origTarget.nodeName?.toLowerCase()) && origTarget.labels?.length) {
    result = origTarget.labels[0].innerText;
  }
  return result;
}

function getText(target: HTMLElement) {
  let result = "";
  if (target.getAttribute("aria-label")) {
    result = target.getAttribute("aria-label")!;
  } else if (target.innerText.trim()) {
    result = target.innerText.trim();
  } else if (target.getAttribute("alt")) {
    result = target.getAttribute("alt")!;
  } else if (target.getAttribute("title")) {
    result = target.getAttribute("title")!;
  }
  return result;
}

function getImageText(target: HTMLImageElement | HTMLTitleElement) {
  return getText(target);
}

function getTextLabel(origTarget: ClickableElement): string {
  let target = origTarget;
  let result = getFormTextLabel(origTarget as FormElement);

  if (result.length) {
    return result;
  }
  result = getText(target);

  if (!result) {
    const imgs = target.querySelectorAll<HTMLImageElement>("img");
    imgs.forEach((img) => {
      result += " " + getImageText(img);
    });
    const svgs = target.querySelectorAll<HTMLTitleElement>("svg title");
    svgs.forEach((s) => {
      result += " " + getImageText(s);
    });
  }

  return result.replaceAll(/\s\s+/g, " ").trim();
}

function shouldTrackClick(target: HTMLElement) {
  const clickedElement = target.closest(clickableElsString) as ClickableElement | null;
  if (!clickedElement) {
    return false;
  }
  const eventName = getEventNameFromTarget(clickedElement, ASSUME_INTERACTION_IF_NOTHING_SPECIFIED);
  if (clickedElement.closest(ignoredElementCSSQuery) || clickedElement.matches(ignoredRuntimeCSSQuery) || !eventName) {
    return false;
  }

  const base = eventName.split(" ")[0];
  if (base === "accordionlist" && !getConfig("trackAccordions")) {
    return false;
  }
  if (base === "cta" && !getConfig("trackCta")) {
    return false;
  }
  if (base === "interaction" && !getConfig("trackInteraction")) {
    return false;
  }
  if (base === "download" && !getConfig("trackDownload")) {
    return false;
  }
  return true;
}

function handleClick(e: Event) {
  try {
    const target = e.currentTarget as HTMLElement | null;
    if (!target || !shouldTrackClick(target)) {
      return;
    }
    const clickedElement = target as ClickableElement;
    const eventName = getEventNameFromTarget(clickedElement, ASSUME_INTERACTION_IF_NOTHING_SPECIFIED);
    const base = eventName.split(" ")[0];

    const eventCategory = getEventCategory(clickedElement);
    const eventAction = "click";
    const eventLabel = getTextLabel(clickedElement);

    const payload: AdobePayload = {
      eventName,
      eventCategory,
      eventAction,
      eventLabel,
      blockName: clickedElement.dataset?.adobeBlockname ?? "",
      blockId: clickedElement.dataset?.adobeBlockid ?? "",
    };

    const href = clickedElement.getAttribute("href") ?? "";
    const isAnchor = href.trim().startsWith("#") || href.includes(window.location.pathname + "#");
    const isFakeLink = href.trim().startsWith("javascript:");
    const isLink = clickedElement.nodeName === "A" && !isAnchor && !isFakeLink;
    const isButton =
      clickedElement.nodeName === "BUTTON" ||
      (clickedElement.nodeName === "INPUT" && (clickedElement as HTMLInputElement).type.toLocaleLowerCase() === "button") ||
      (clickedElement.nodeName === "INPUT" && (clickedElement as HTMLInputElement).type.toLocaleLowerCase() === "submit");
    const isFormSubmitButton = isButton && (target as HTMLButtonElement).type === "submit";
    const isDefaultPrevented = e.defaultPrevented;

    // https://stackoverflow.com/a/58026592/846348
    // We don't need to wait for any answer from server as long as we sent the request
    // window._satellite.track does not have any callback or promise though so we need to wait a random time
    let waitForTrackingBeforeFollowingLinks = (cb: () => void) => {
      const clonedNativeEvent = new MouseEvent("click", e);
      if (clickedElement.dataset?.fakeClick === "true") {
        clickedElement.dataset.fakeClick = "false";
        return;
      }
      e.preventDefault();
      cb();
      clickedElement.dataset.fakeClick = "true";

      setTimeout(() => {
        target.dispatchEvent(clonedNativeEvent);
      }, 300);
    };

    if (isLink || (isFormSubmitButton && !isDefaultPrevented)) {
      waitForTrackingBeforeFollowingLinks(() => trackEvent(base, payload));
    } else {
      trackEvent(base, payload);
    }
  } catch (err) {
    console.warn("Tracking error:", err);
  }
}

const hasAcceptedAnalyticalCookies = () => {
  const re = /(?:^| )CookieConsent=([^;]+)/;
  const match = re.exec(document.cookie);
  if (!match) {
    return;
  }
  const cookieDecoded = decodeURIComponent(match[1]);
  const cookieParsed = JSON.parse(cookieDecoded);
  let analyticalGroup = null;

  if (Array.isArray(cookieParsed?.["cookies-groups"])) {
    for (const group of cookieParsed["cookies-groups"]) {
      if (group?.name === "analyticalcookies") {
        analyticalGroup = group;
      }
    }
  }

  return analyticalGroup?.approved;
};

function Tracking() {
  try {
    // add rel noopener for security on links that opens in a new tab
    // dirty fix because we don´t fix it in BE
    // this script should take into account if it is fixed in BE later also
    //  does not take into account if it is malformed from BE like:
    //  rel="noopenernoreferrer" (missing space)
    //  rel="noopenerinvalidstring"
    document.querySelectorAll<HTMLAnchorElement>(`a[target="_blank"]:not([rel*="noopener"])`).forEach((a) => {
      if (!a.href?.includes(window.location.host)) {
        let rel = a.getAttribute("rel")?.trim() ?? "";
        rel += " noopener";
        a.dataset.jsModified = "1";
        a.setAttribute("rel", rel.trim());
      }
    });

    const adobeLoaded = window._satellite?.track;
    if (!adobeLoaded && !isLocalHostStyleguide) {
      return;
    }
    if (!hasAcceptedAnalyticalCookies() && !isLocalHostStyleguide) {
      return;
    }
    document.querySelectorAll(clickableElsString).forEach((el) => {
      el.removeEventListener("click", handleClick, true);
      el.addEventListener("click", handleClick, true);
    });
  } catch {
    return;
  }
}
Tracking();

// Needed for javascript updated DOM example Telia Ace / Sofi
function watchJavascriptGeneratedContent() {
  const observer = new MutationObserver(() => {
    Tracking();
  });
  observer.observe(document.body, { attributes: false, childList: true, subtree: true });
}
watchJavascriptGeneratedContent();
